const HOST_INVALID_CHARS_REGEX = /[^a-zA-Z0-9.:-]/g

/**
 * Removes ASCII control characters from input values.
 *
 * Why this matters:
 * - Control chars can be used to obfuscate payloads.
 * - They can also create parser inconsistencies between environments.
 *
 * Kept chars:
 * - Printable ASCII (>= 32)
 * - Excludes DEL (127)
 */
const stripControlChars = (value = '') => {
    return Array.from(String(value))
        .filter((char) => {
            const charCode = char.charCodeAt(0)
            return charCode >= 32 && charCode !== 127
        })
        .join('')
}

/**
 * Tries to decode URI sequences once.
 *
 * Why this matters:
 * - Accepts inputs that are already percent-encoded.
 * - Avoids throwing on malformed encodings by falling back to raw input.
 */
const decodeUriSafely = (value = '') => {
    try {
        return decodeURI(value)
    } catch (_error) {
        return value
    }
}

/**
 * Sanitizes host input used in virtualUrl.
 *
 * Behavior:
 * - Removes control chars.
 * - Trims surrounding whitespace.
 * - Keeps only host-safe characters (letters, digits, dot, colon, dash).
 *
 * Note:
 * - Intentionally strict to avoid malformed host injection in analytics URLs.
 */
export const sanitizeHost = (host = '') => {
    const normalizedHost = stripControlChars(host).trim().replace(HOST_INVALID_CHARS_REGEX, '')
    return normalizedHost
}

/**
 * Sanitizes pathname input.
 *
 * Behavior:
 * - Removes control chars.
 * - Ensures pathname always starts with '/'.
 * - Uses the URL parser to normalize and percent-encode unsafe characters.
 *
 * Security goal:
 * - Prevent raw path fragments like '</script>' from flowing into script-adjacent
 *   payloads unencoded.
 */
export const sanitizePathname = (pathname = '/') => {
    const normalizedPathname = stripControlChars(pathname || '/')
    const pathWithLeadingSlash = normalizedPathname.startsWith('/')
        ? normalizedPathname
        : `/${normalizedPathname}`
    // Base URL is only a parser anchor for pathname normalization.
    // It is never returned to callers.
    const safeUrl = new URL('https://sanitizer.local')
    safeUrl.pathname = decodeUriSafely(pathWithLeadingSlash)

    return safeUrl.pathname
}

/**
 * Sanitizes the query string.
 *
 * Behavior:
 * - Removes control chars.
 * - Accepts values with or without leading '?'.
 * - Rebuilds query via URLSearchParams to normalize encoding.
 * - Returns '' when query is empty.
 *
 * Output format:
 * - Either '' or '?key=value...'
 */
export const sanitizeSearch = (search = '') => {
    const normalizedSearch = stripControlChars(search)
    const rawSearch = normalizedSearch.startsWith('?')
        ? normalizedSearch.slice(1)
        : normalizedSearch

    if (!rawSearch) {
        return ''
    }

    const searchParams = new URLSearchParams(rawSearch)
    const serializedSearch = searchParams.toString()

    return serializedSearch ? `?${serializedSearch}` : ''
}

/**
 * Convenience helper that sanitizes pathname and search together.
 *
 * Useful for call sites that need relative URL parts without host/protocol.
 */
export const getSanitizedPathAndSearch = ({pathname = '/', search = ''} = {}) => {
    return {
        pathname: sanitizePathname(pathname),
        search: sanitizeSearch(search),
    }
}

/**
 * Builds a sanitized virtual URL used in analytics events.
 *
 * Behavior:
 * - Sanitizes pathname and search components.
 * - Sanitizes host when present.
 * - Allows only 'http:' or 'https:' protocol (defaults to 'https:').
 * - Falls back to relative path+search when host is missing.
 *
 * Security goal:
 * - Centralize URL construction so untrusted location values are always normalized
 *   before they are included in tracking payloads.
 */
export const buildSanitizedVirtualUrl = ({
    host,
    pathname = '/',
    search = '',
    protocol = 'https:',
} = {}) => {
    const {pathname: safePathname, search: safeSearch} = getSanitizedPathAndSearch({
        pathname,
        search,
    })
    const safeHost = sanitizeHost(host)
    const safeProtocol = protocol === 'http:' ? 'http:' : 'https:'

    if (!safeHost) {
        return `${safePathname}${safeSearch}`
    }

    return `${safeProtocol}//${safeHost}${safePathname}${safeSearch}`
}
